
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Home() {
  const [searchQuery, setSearchQuery] = useState('');
  const [cartCount, setCartCount] = useState(0);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const updateCartCount = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        try {
          const cart = JSON.parse(savedCart);
          const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
          setCartCount(count);
        } catch (error) {
          console.error('Error parsing cart:', error);
          localStorage.removeItem('cart');
          setCartCount(0);
        }
      } else {
        setCartCount(0);
      }
    };
    
    const checkUser = () => {
      const user = localStorage.getItem('currentUser');
      if (user) {
        try {
          setCurrentUser(JSON.parse(user));
        } catch (error) {
          console.error('Error parsing user:', error);
          localStorage.removeItem('currentUser');
        }
      }
    };
    
    updateCartCount();
    checkUser();
    
    window.addEventListener('storage', updateCartCount);
    
    const handleCartUpdate = () => updateCartCount();
    window.addEventListener('cartUpdated', handleCartUpdate);

    const timer = setTimeout(() => {
      setShowNewsletter(true);
    }, 5000);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', handleCartUpdate);
      clearTimeout(timer);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
    setShowUserMenu(false);
  };

  const addToCart = (product: any) => {
    const savedCart = localStorage.getItem('cart');
    const cart = savedCart ? JSON.parse(savedCart) : [];
    
    const existingItem = cart.find((item: any) => item.id === product.id);
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }
    
    localStorage.setItem('cart', JSON.stringify(cart));
    const newCount = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
    setCartCount(newCount);
    
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/all-products?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('email', newsletterEmail);

      const response = await fetch('https://readdy.ai/api/form/d4k346se64en0qhd59ig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (response.ok) {
        setNewsletterSuccess(true);
        setNewsletterEmail('');
        setShowNewsletter(false);
        setTimeout(() => setNewsletterSuccess(false), 3000);
      } else {
        console.error('Newsletter submission failed:', response.status);
      }
    } catch (error) {
      console.error('Error submitting newsletter:', error);
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const categories = [
    { id: 1, name: "Men's Health", icon: 'ri-men-line', color: 'bg-blue-50', iconColor: 'text-blue-600', link: '/category/mens-health' },
    { id: 2, name: 'Pain Relief', icon: 'ri-first-aid-kit-line', color: 'bg-red-50', iconColor: 'text-red-600', link: '/category/pain-relief' },
    { id: 3, name: 'Diabetes', icon: 'ri-heart-pulse-line', color: 'bg-green-50', iconColor: 'text-green-600', link: '/category/diabetes' },
    { id: 4, name: 'Heart Health', icon: 'ri-heart-line', color: 'bg-pink-50', iconColor: 'text-pink-600', link: '/category/heart-health' },
    { id: 5, name: 'Skin Care', icon: 'ri-contrast-drop-line', color: 'bg-purple-50', iconColor: 'text-purple-600', link: '/category/skin-care' },
    { id: 6, name: 'Antibiotics', icon: 'ri-capsule-line', color: 'bg-orange-50', iconColor: 'text-orange-600', link: '/category/antibiotics' },
    { id: 7, name: 'Sexual Wellness', icon: 'ri-heart-pulse-fill', color: 'bg-rose-50', iconColor: 'text-rose-600', link: '/category/sexual-wellness' },
    { id: 8, name: 'Vitamins & Supplements', icon: 'ri-flask-line', color: 'bg-yellow-50', iconColor: 'text-yellow-600', link: '/category/vitamins' },
  ];

  const bestSellers = [
    {
      id: 1,
      name: 'Viagra 100mg',
      category: "Men's Health",
      price: 89.99,
      originalPrice: 129.99,
      image: 'https://public.readdy.ai/ai/img_res/23f6d42d6cc4de66a1d16f4dc7183416.jpg',
      rating: 4.8,
      reviews: 2847,
      discount: 31,
      inStock: true,
      link: '/products/viagra-100mg'
    },
    {
      id: 2,
      name: 'Cialis 20mg',
      category: "Men's Health",
      price: 94.99,
      originalPrice: 139.99,
      image: 'https://readdy.ai/api/search-image?query=professional%20cialis%20tadalafil%20tablets%20in%20pharmaceutical%20blister%20pack%20on%20clean%20white%20background%20medical%20product%20photography%20soft%20lighting%20high%20quality%20shot&width=400&height=400&seq=2&orientation=squarish',
      rating: 4.9,
      reviews: 3156,
      discount: 32,
      inStock: true,
      link: '/products/cialis-20mg'
    },
    {
      id: 3,
      name: 'Soma 350mg',
      category: 'Pain Relief',
      price: 79.99,
      originalPrice: 119.99,
      image: 'https://readdy.ai/api/search-image?query=professional%20soma%20carisoprodol%20muscle%20relaxer%20tablets%20in%20pharmaceutical%20packaging%20on%20clean%20white%20surface%20medical%20product%20photography%20soft%20lighting%20high%20quality%20shot&width=400&height=400&seq=5&orientation=squarish',
      rating: 4.7,
      reviews: 1923,
      discount: 33,
      inStock: true,
      link: '/products/soma-350mg'
    },
    {
      id: 4,
      name: 'Tapentadol 100mg',
      category: 'Pain Relief',
      price: 99.99,
      originalPrice: 149.99,
      image: 'https://readdy.ai/api/search-image?query=professional%20tapentadol%20pain%20relief%20tablets%20in%20pharmaceutical%20packaging%20on%20clean%20white%20surface%20medical%20product%20photography%20soft%20lighting%20high%20quality%20shot&width=400&height=400&seq=110&orientation=squarish',
      rating: 4.8,
      reviews: 2134,
      discount: 33,
      inStock: true,
      link: '/products/tapentadol-100mg'
    },
    {
      id: 5,
      name: 'Levitra 20mg',
      category: "Men's Health",
      price: 92.99,
      originalPrice: 134.99,
      image: 'https://readdy.ai/api/search-image?query=professional%20levitra%20vardenafil%20tablets%20in%20pharmaceutical%20packaging%20on%20clean%20white%20surface%20medical%20product%20photography%20soft%20lighting%20high%20quality%20shot&width=400&height=400&seq=3&orientation=squarish',
      rating: 4.7,
      reviews: 1876,
      discount: 31,
      inStock: true,
      link: '/products/levitra-20mg'
    },
    {
      id: 6,
      name: 'Kamagra Oral Jelly',
      category: "Men's Health",
      price: 69.99,
      originalPrice: 99.99,
      image: 'https://readdy.ai/api/search-image?query=professional%20kamagra%20oral%20jelly%20sachets%20colorful%20packaging%20on%20clean%20white%20background%20pharmaceutical%20product%20photography%20soft%20lighting%20high%20quality%20shot&width=400&height=400&seq=4&orientation=squarish',
      rating: 4.6,
      reviews: 1654,
      discount: 30,
      inStock: true,
      link: '/products/kamagra-oral-jelly'
    },
  ];

  const reviews = [
    {
      id: 1,
      name: 'Michael Thompson',
      rating: 5,
      comment: 'Fast delivery and genuine products. I have been ordering from PillsForU for over a year now. Highly recommended!',
      date: '2 days ago',
      verified: true
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      rating: 5,
      comment: 'Excellent customer service and quality medications. The prices are very competitive compared to local pharmacies.',
      date: '5 days ago',
      verified: true
    },
    {
      id: 3,
      name: 'David Martinez',
      rating: 5,
      comment: 'Secure packaging and discreet shipping. Products arrived exactly as described. Will definitely order again.',
      date: '1 week ago',
      verified: true
    },
    {
      id: 4,
      name: 'Emily Chen',
      rating: 4,
      comment: 'Great experience overall. Delivery took a bit longer than expected but the product quality is excellent.',
      date: '2 weeks ago',
      verified: true
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Newsletter Popup */}
      {showNewsletter && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative">
            <button 
              onClick={() => setShowNewsletter(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            <div className="text-center mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                <i className="ri-mail-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Get 15% Off Your First Order!</h3>
              <p className="text-gray-600">Subscribe to our newsletter for exclusive deals and health tips</p>
            </div>
            <form onSubmit={handleNewsletterSubmit} data-readdy-form>
              <input
                type="email"
                name="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4 text-sm"
              />
              <button 
                type="submit"
                disabled={newsletterSubmitting}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {newsletterSubmitting ? 'Subscribing...' : 'Subscribe Now'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div className="bg-blue-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <span className="flex items-center">
                <i className="ri-phone-line mr-2"></i>
                +1 (929) 268-8770
              </span>
              <span className="flex items-center">
                <i className="ri-mail-line mr-2"></i>
                sam4104t@gmail.com
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="flex items-center">
                <i className="ri-truck-line mr-2"></i>
                Free Worldwide Shipping
              </span>
              <span className="flex items-center">
                <i className="ri-shield-check-line mr-2"></i>
                100% Secure Checkout
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-lg mr-3">
                <i className="ri-medicine-bottle-line text-2xl text-white"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-blue-600" style={{ fontFamily: '"Inter", sans-serif' }}>PillsForU</h1>
                <p className="text-xs text-gray-500">Trusted Healthcare</p>
              </div>
            </Link>

            {/* Search Bar */}
            <div className="hidden lg:flex flex-1 max-w-2xl mx-8">
              <div className="w-full relative">
                <input
                  type="text"
                  placeholder="Search for medicines, health products..."
                  className="w-full pl-12 pr-4 py-3 border-2 border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                />
                <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl"></i>
                <button 
                  onClick={handleSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer font-semibold text-sm"
                >
                  Search
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <Link to="/cart" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors cursor-pointer relative">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-2xl"></i>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{cartCount}</span>
                  )}
                </div>
                <div className="ml-2 hidden md:block">
                  <p className="text-xs text-gray-500">Cart</p>
                  <p className="text-sm font-semibold">{cartCount} items</p>
                </div>
              </Link>

              {currentUser ? (
                <div className="relative">
                  <button 
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <div className="w-10 h-10 flex items-center justify-center text-white bg-blue-600 rounded-full hover:bg-blue-700 transition-colors font-semibold">
                      {currentUser.firstName?.charAt(0)}{currentUser.lastName?.charAt(0)}
                    </div>
                    <div className="hidden md:block text-left">
                      <p className="text-xs text-gray-500">Hello,</p>
                      <p className="text-sm font-semibold text-gray-900">{currentUser.firstName}</p>
                    </div>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">{currentUser.firstName} {currentUser.lastName}</p>
                        <p className="text-xs text-gray-600 mt-1">{currentUser.email}</p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex items-center"
                      >
                        <i className="ri-logout-box-line mr-2"></i>
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/auth" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-user-line text-2xl"></i>
                  </div>
                  <div className="ml-2 hidden md:block">
                    <p className="text-xs text-gray-500">Hello, Sign in</p>
                    <p className="text-sm font-semibold">Account</p>
                  </div>
                </Link>
              )}
            </div>
          </div>

          {/* Mobile Search */}
          <div className="lg:hidden pb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search medicines..."
                className="w-full pl-10 pr-4 py-2 border-2 border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
              />
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>
        </div>
      </nav>

      {/* Category Menu */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-8 py-3 overflow-x-auto">
            <Link to="/all-products" className="text-sm font-semibold text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              All Products
            </Link>
            <Link to="/category/mens-health" className="text-sm text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Men's Health
            </Link>
            <Link to="/category/pain-relief" className="text-sm text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Pain Relief
            </Link>
            <Link to="/category/diabetes" className="text-sm text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Diabetes
            </Link>
            <Link to="/category/heart-health" className="text-sm text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Heart Health
            </Link>
            <Link to="/category/skin-care" className="text-sm text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Skin Care
            </Link>
            <Link to="/about" className="text-sm text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              About Us
            </Link>
            <Link to="/contact" className="text-sm text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Contact
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-600 to-blue-800 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">Fast &amp; Affordable Medicines Delivered Worldwide</h2>
              <p className="text-xl mb-8 text-blue-100">Trusted by over 500,000 customers worldwide. Get genuine medications at the best prices with secure, discreet shipping to your doorstep.</p>
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link to="/all-products" className="bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer font-bold text-center text-lg shadow-lg">
                  Shop Now
                </Link>
                <Link to="/all-products" className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer font-bold text-center text-lg">
                  View Categories
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-3">
                    <i className="ri-shield-check-line text-3xl"></i>
                  </div>
                  <p className="font-semibold">100% Genuine</p>
                  <p className="text-sm text-blue-100">FDA Approved</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-3">
                    <i className="ri-truck-line text-3xl"></i>
                  </div>
                  <p className="font-semibold">Fast Delivery</p>
                  <p className="text-sm text-blue-100">7-15 Days</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-3">
                    <i className="ri-customer-service-2-line text-3xl"></i>
                  </div>
                  <p className="font-semibold">24/7 Support</p>
                  <p className="text-sm text-blue-100">Always Here</p>
                </div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20online%20pharmacy%20concept%20with%20professional%20pharmacist%20in%20white%20coat%20holding%20medicine%20bottles%20and%20digital%20tablet%20in%20bright%20clean%20pharmacy%20interior%20with%20medicine%20shelves%20healthcare%20technology%20medical%20professional%20photography%20high%20quality%20blue%20and%20white%20theme&width=600&height=600&seq=hero1&orientation=squarish" 
                alt="Professional Pharmacy" 
                className="rounded-2xl shadow-2xl object-cover w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="bg-white py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center justify-center space-x-3">
              <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full">
                <i className="ri-verified-badge-line text-2xl text-green-600"></i>
              </div>
              <div>
                <p className="font-bold text-gray-900">FDA Approved</p>
                <p className="text-xs text-gray-600">Certified Products</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full">
                <i className="ri-lock-line text-2xl text-blue-600"></i>
              </div>
              <div>
                <p className="font-bold text-gray-900">Secure Payment</p>
                <p className="text-xs text-gray-600">SSL Encrypted</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-full">
                <i className="ri-refund-line text-2xl text-purple-600"></i>
              </div>
              <div>
                <p className="font-bold text-gray-900">Money Back</p>
                <p className="text-xs text-gray-600">30-Day Guarantee</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full">
                <i className="ri-global-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <p className="font-bold text-gray-900">Worldwide</p>
                <p className="text-xs text-gray-600">Free Shipping</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popular Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Shop by Category</h2>
          <p className="text-lg text-gray-600">Browse our wide range of healthcare products</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {categories.map((category) => (
            <Link
              key={category.id}
              to={category.link}
              className={`${category.color} rounded-xl p-8 hover:shadow-xl transition-all cursor-pointer group border-2 border-transparent hover:border-blue-200`}
            >
              <div className={`w-16 h-16 flex items-center justify-center ${category.iconColor} mb-4 mx-auto`}>
                <i className={`${category.icon} text-4xl`}></i>
              </div>
              <h3 className="text-center font-bold text-gray-900 group-hover:text-blue-600 transition-colors text-lg">{category.name}</h3>
            </Link>
          ))}
        </div>
      </section>

      {/* Best Sellers */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">Best Sellers</h2>
              <p className="text-lg text-gray-600">Top-rated medicines trusted by thousands</p>
            </div>
            <Link to="/all-products" className="text-blue-600 font-semibold hover:text-blue-700 cursor-pointer flex items-center">
              View All
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
            {bestSellers.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all group cursor-pointer border border-gray-200"
              >
                <Link to={product.link} className="block" onClick={() => window.scrollTo(0, 0)}>
                  <div className="relative overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-72 object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                    {product.discount > 0 && (
                      <span className="absolute top-4 left-4 bg-red-500 text-white text-sm px-3 py-1 rounded-full font-bold">
                        {product.discount}% OFF
                      </span>
                    )}
                    {product.inStock && (
                      <span className="absolute top-4 right-4 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-semibold">In Stock</span>
                    )}
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-blue-600 font-semibold mb-2">{product.category}</p>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{product.name}</h3>
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`${
                              i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'
                            } text-yellow-400 text-sm`}
                          ></i>
                        ))}
                      </div>
                      <span className="ml-2 text-sm text-gray-600">({product.reviews})</span>
                    </div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-2xl font-bold text-gray-900">${product.price}</p>
                        {product.originalPrice && (
                          <p className="text-sm text-gray-500 line-through">${product.originalPrice}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
                <div className="px-6 pb-6">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      addToCart(product);
                    }}
                    className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer font-semibold"
                  >
                    <i className="ri-shopping-cart-line mr-2"></i>
                    Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Discount Offers */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-12 text-white text-center">
          <h2 className="text-4xl font-bold mb-4">Limited Time Offer!</h2>
          <p className="text-xl mb-8">Get up to 40% OFF on selected medicines. Free shipping on orders over $100!</p>
          <Link to="/all-products" className="bg-white text-orange-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer font-bold text-lg inline-block shadow-lg">
            Shop Deals Now
          </Link>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="text-lg text-gray-600">Over 50,000+ satisfied customers worldwide</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviews.map((review) => (
              <div key={review.id} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`${
                          i < review.rating ? 'ri-star-fill' : 'ri-star-line'
                        } text-yellow-400 text-sm`}
                      ></i>
                    ))}
                  </div>
                  {review.verified && (
                    <span className="ml-2 text-xs text-green-600 font-semibold flex items-center">
                      <i className="ri-checkbox-circle-fill mr-1"></i>
                      Verified
                    </span>
                  )}
                </div>
                <p className="text-gray-700 mb-4 text-sm leading-relaxed">{review.comment}</p>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-gray-900">{review.name}</p>
                  <p className="text-xs text-gray-500">{review.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose PillsForU?</h2>
          <p className="text-lg text-gray-600">Your health and satisfaction are our top priorities</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center p-8 bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow border border-gray-200">
            <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-6">
              <i className="ri-verified-badge-line text-4xl text-blue-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">100% Genuine Products</h3>
            <p className="text-gray-600 leading-relaxed">All our medicines are sourced directly from licensed manufacturers and verified for authenticity. FDA approved and quality guaranteed.</p>
          </div>
          <div className="text-center p-8 bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow border border-gray-200">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
              <i className="ri-customer-service-2-line text-4xl text-green-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">24/7 Customer Support</h3>
            <p className="text-gray-600 leading-relaxed">Our licensed pharmacists and support team are available round the clock to answer your questions and provide expert guidance.</p>
          </div>
          <div className="text-center p-8 bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow border border-gray-200">
            <div className="w-20 h-20 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-6">
              <i className="ri-price-tag-3-line text-4xl text-purple-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Best Prices Guaranteed</h3>
            <p className="text-gray-600 leading-relaxed">Competitive pricing with regular discounts and offers. Save up to 70% compared to local pharmacy prices with free worldwide shipping.</p>
          </div>
        </div>
      </section>

      {/* Shipping Info */}
      <section className="bg-blue-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white text-center">
            <div>
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-global-line text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Worldwide Delivery</h3>
              <p className="text-blue-100">Fast and reliable shipping to over 150 countries. Track your order in real-time.</p>
            </div>
            <div>
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-lock-line text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Secure Checkout</h3>
              <p className="text-blue-100">SSL encrypted payment gateway. Your personal and payment information is always protected.</p>
            </div>
            <div>
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-customer-service-2-line text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">24/7 Support</h3>
              <p className="text-blue-100">Expert pharmacists available anytime. Get answers to your health questions instantly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-lg mr-3">
                  <i className="ri-medicine-bottle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">PillsForU</h3>
                  <p className="text-xs text-gray-400">Trusted Healthcare</p>
                </div>
              </div>
              <p className="text-gray-400 mb-4 leading-relaxed">Your trusted partner in healthcare, delivering quality medicines worldwide. Licensed, certified, and committed to your health and wellbeing.</p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4 text-lg">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="hover:text-blue-400 transition-colors cursor-pointer">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-blue-400 transition-colors cursor-pointer">Contact Us</Link></li>
                <li><Link to="/all-products" className="hover:text-blue-400 transition-colors cursor-pointer">All Products</Link></li>
                <li><Link to="/faq" className="hover:text-blue-400 transition-colors cursor-pointer">FAQs</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4 text-lg">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Privacy Policy</Link></li>
                <li><Link to="/terms-conditions" className="hover:text-blue-400 transition-colors cursor-pointer">Terms &amp; Conditions</Link></li>
                <li><Link to="/refund-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Refund Policy</Link></li>
                <li><Link to="/shipping-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Shipping Policy</Link></li>
                <li><Link to="/medical-disclaimer" className="hover:text-blue-400 transition-colors cursor-pointer">Medical Disclaimer</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4 text-lg">Contact Info</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-phone-line mt-1 mr-3 text-blue-400"></i>
                  <div>
                    <p className="font-semibold text-white">Phone</p>
                    <span>+1 (929) 268-8770</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-mail-line mt-1 mr-3 text-blue-400"></i>
                  <div>
                    <p className="font-semibold text-white">Email</p>
                    <span>sam4104t@gmail.com</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-whatsapp-line mt-1 mr-3 text-blue-400"></i>
                  <div>
                    <p className="font-semibold text-white">WhatsApp</p>
                    <span>+1 (929) 268-8770</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">&copy; 2024 PillsForU.com. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-blue-400 transition-colors cursor-pointer">Powered by Readdy</a></p>
              <div className="flex items-center space-x-4">
                <img src="https://readdy.ai/api/search-image?query=visa%20mastercard%20payment%20icons%20white%20on%20transparent%20background&width=50&height=30&seq=visa&orientation=squarish" alt="Payment Methods" className="h-8" />
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
